import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ChevronRightOutline,
  LockOutline,
  LogoutOutline,
  NotificationBingOutline,
  PhoneOutline,
  SmsOutline,
  UserOutline,
} from '@aslanpay/portal-common-lib';
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import PortalSwitcherMenuItem from 'app/components/Navbar/PortalSwitcherMenuItem';
import useAuthContext from 'app/hooks/useAuthContext';
import useNavbarActions from 'app/hooks/useNavbarActions';
import { authenticatedPaths } from 'app/constants/routes';
import { getInitials } from 'app/utils/initials';

import useNotificationBadge from './useNotificationBadge';

// Labels carry the meaning in this menu, so the icons sit back in a muted grey.
const MUTED_ICON = 'brand.mutedIcon';
const MENU_TEXT = 'brand.menuText';

const SUPPORT_MAILTO = 'mailto:support@aslan.io';

interface AccountAction {
  key: string;
  label: string;
  icon: ReactElement;
  onClick?: () => void;
  /** Set instead of onClick for mail and external links. */
  href?: string;
  /** Shown at the end of the row, e.g. the unread count. */
  trailing?: ReactElement;
}

interface AccountRowProps {
  /** Narrow screens open a bottom sheet, which a pop-up panel would clip. */
  variant: 'flyout' | 'sheet';
}

function AccountRow({ variant }: AccountRowProps) {
  const { activeLoginContext } = useAuthContext();
  const {
    user,
    company,
    isSSOUser,
    openModal,
    handleLogout,
    handleProfileClick,
    handleChangePasswordClick,
  } = useNavbarActions();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { count } = useNotificationBadge();
  const navigate = useNavigate();

  const name =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email ?? '';

  const actions: AccountAction[] = [
    {
      key: 'profile',
      label: 'My profile',
      icon: <UserOutline boxSize="18px" color={MUTED_ICON} />,
      onClick: handleProfileClick,
    },
  ];

  if (!isSSOUser) {
    actions.push({
      key: 'password',
      label: `Change ${activeLoginContext === 'EMPLOYEE' ? 'passcode' : 'password'}`,
      icon: <LockOutline boxSize="18px" color={MUTED_ICON} />,
      onClick: handleChangePasswordClick,
    });
  }

  if (user && !user.phoneNumberVerified && company?.type === 'GIFTING_ONLY') {
    actions.push({
      key: 'phone',
      label: 'Verify phone number',
      icon: <PhoneOutline boxSize="18px" color={MUTED_ICON} />,
      onClick: openModal,
    });
  }

  // Notifications and Contact Aslan are errands rather than account settings, so they sit
  // in their own group at the foot of the menu, just above signing out.
  const utilities: AccountAction[] = [
    {
      key: 'notifications',
      label: 'Notifications',
      icon: <NotificationBingOutline boxSize="18px" color={MUTED_ICON} />,
      onClick: () => navigate(authenticatedPaths.notifications.path),
      trailing:
        count > 0 ? (
          <Flex
            data-testid="account-notification-count"
            aria-label={`${count} unread`}
            as="span"
            display="inline-flex"
            align="center"
            justify="center"
            flexShrink={0}
            minW="20px"
            h="20px"
            px="6px"
            borderRadius="10px"
            bg="system.error"
            color="brand.white"
            fontSize="11px"
            fontWeight="semibold"
            lineHeight="1"
          >
            {count > 99 ? '99+' : count}
          </Flex>
        ) : undefined,
    },
    {
      key: 'contact',
      label: 'Contact Aslan',
      icon: <SmsOutline boxSize="18px" color={MUTED_ICON} />,
      href: SUPPORT_MAILTO,
    },
  ];

  // The label and its badge share one row: the label takes the slack so the badge is
  // pushed to the right edge and only ever as wide as the number it holds.
  const actionContent = (action: AccountAction) => (
    <Flex as="span" align="center" gap="8px" w="full" minW="0">
      <Box as="span" flex="1" minW="0" textAlign="left">
        {action.label}
      </Box>
      {action.trailing}
    </Flex>
  );

  const rowContent = (isPanelOpen: boolean) => (
    <Flex align="center" gap="8px" w="full" minW="0">
      <Flex
        align="center"
        justify="center"
        boxSize="24px"
        borderRadius="full"
        bg="brand.avatarLilac"
        color="brand.black"
        flexShrink={0}
        aria-hidden="true"
      >
        <Text textStyle="extraExtraSmallMedium">
          {getInitials(user?.firstName, user?.lastName, user?.email)}
        </Text>
      </Flex>

      <Box flex="1" minW="0" textAlign="left">
        <Text textStyle="extraSmallSemiBold" color="brand.black" isTruncated>
          {name}
        </Text>
        <Text textStyle="extraExtraSmallMedium" color="brand.darkGray">
          Account
        </Text>
      </Box>

      {/* A right chevron reads as "this opens something", and goes purple while it is —
          the panel itself opens upward, over the sidebar. */}
      <ChevronRightOutline
        boxSize="16px"
        color={isPanelOpen ? 'brand.indigo' : 'brand.darkPurple'}
        flexShrink={0}
      />
    </Flex>
  );

  const ROW_STYLES = {
    align: 'center',
    gap: '8px',
    w: 'full',
    minH: '44px',
    px: '8px',
    py: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    _hover: { bg: 'brand.navMuted' },
    _focusVisible: { outline: '2px solid', outlineColor: 'brand.indigo', outlineOffset: '-2px' },
  } as const;

  if (variant === 'sheet') {
    return (
      <>
        <Flex
          as="button"
          type="button"
          {...ROW_STYLES}
          onClick={onOpen}
          data-testid="sidebar-profile-button"
        >
          {rowContent(isOpen)}
        </Flex>

        <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent borderTopRadius="16px" pb="16px" data-testid="account-sheet">
            <Flex direction="column" p="12px" gap="2px">
              {actions.map((action) => (
                <Button
                  key={action.key}
                  {...(action.href ? { as: 'a' as const, href: action.href } : {})}
                  leftIcon={action.icon}
                  variant="ghost"
                  justifyContent="flex-start"
                  w="full"
                  minH="48px"
                  borderRadius="8px"
                  fontWeight="medium"
                  color={MENU_TEXT}
                  _hover={{ bg: 'brand.lightGray2' }}
                  onClick={() => {
                    onClose();
                    action.onClick?.();
                  }}
                >
                  {actionContent(action)}
                </Button>
              ))}

              <PortalSwitcherMenuItem renderAs="row" tone="muted" />

              <Divider borderColor="brand.lightGray1" my="8px" />

              {utilities.map((action) => (
                <Button
                  key={action.key}
                  {...(action.href ? { as: 'a' as const, href: action.href } : {})}
                  leftIcon={action.icon}
                  variant="ghost"
                  justifyContent="flex-start"
                  w="full"
                  minH="48px"
                  borderRadius="8px"
                  fontWeight="medium"
                  color={MENU_TEXT}
                  _hover={{ bg: 'brand.lightGray2' }}
                  onClick={() => {
                    onClose();
                    action.onClick?.();
                  }}
                  data-testid={`account-${action.key}`}
                >
                  {actionContent(action)}
                </Button>
              ))}

              <Divider borderColor="brand.lightGray1" my="8px" />

              <Button
                leftIcon={<LogoutOutline boxSize="18px" color={MUTED_ICON} />}
                variant="ghost"
                justifyContent="flex-start"
                w="full"
                minH="48px"
                borderRadius="8px"
                fontWeight="medium"
                color={MENU_TEXT}
                _hover={{ bg: 'brand.lightGray2' }}
                onClick={() => {
                  onClose();
                  handleLogout();
                }}
                data-testid="account-sign-out"
              >
                Sign out
              </Button>
            </Flex>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  return (
    <Menu placement="top-start" onOpen={onOpen} onClose={onClose}>
      <MenuButton
        as={Flex}
        role="button"
        tabIndex={0}
        {...ROW_STYLES}
        data-testid="sidebar-profile-button"
      >
        {rowContent(isOpen)}
      </MenuButton>

      <MenuList bg="white" border="1px solid" borderColor="brand.lightGray1" boxShadow="lg">
        {actions.map((action) => (
          <MenuItem
            key={action.key}
            {...(action.href ? { as: 'a' as const, href: action.href } : {})}
            onClick={action.onClick}
            icon={action.icon}
            color={MENU_TEXT}
            _hover={{ bg: 'brand.lightGray2' }}
            data-testid={`account-${action.key}`}
          >
            {actionContent(action)}
          </MenuItem>
        ))}

        <PortalSwitcherMenuItem tone="muted" />

        <MenuDivider />

        {utilities.map((action) => (
          <MenuItem
            key={action.key}
            {...(action.href ? { as: 'a' as const, href: action.href } : {})}
            onClick={action.onClick}
            icon={action.icon}
            color={MENU_TEXT}
            _hover={{ bg: 'brand.lightGray2' }}
            data-testid={`account-${action.key}`}
          >
            {actionContent(action)}
          </MenuItem>
        ))}

        {/* Set apart as the last item so it is easy to find, but not red: signing out
            is reversible, and red belongs to genuinely destructive actions. */}
        <MenuDivider />
        <MenuItem
          onClick={handleLogout}
          icon={<LogoutOutline boxSize="16px" color={MUTED_ICON} />}
          color={MENU_TEXT}
          _hover={{ bg: 'brand.lightGray2' }}
          data-testid="account-sign-out"
        >
          Sign out
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

export default AccountRow;
